/* eslint-disable @typescript-eslint/naming-convention */
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Inject,
  Type,
} from '@angular/core';
import {FrequencyUnit, UnitChoicesService, UnitType} from 'src/api';
import {lastValueFrom} from 'rxjs';
import {AWCListContent, AWCListItem} from '@ansys-internal/awc-angular/lists';
import {FlexLayout} from '@ansys-internal/awc-angular/core';
import {Icons} from '@ansys-internal/awc-angular/icons';
import {ConceptService} from 'src/app/shared/services/concept.service';
import {
  DIALOG_REF,
  SnackBarService,
} from '@ansys-internal/awf-angular/shared';
import {State, StateNode} from '@ansys-internal/awf-angular/store';
import {ConceptSections} from 'src/app/shared/enums/concept.enum';
import {UnitChoice} from 'src/app/shared/types';
import {UnitNameTransformPipe} from 'src/app/shared/pipes/unit-name/unit-name-transform.pipe';
import {ActionContributionsService} from '@ansys-internal/awf-angular/contributions';
import {ActiveTabState} from '@ansys-internal/awf-angular/workspace';
import {UpdateInputs} from '../../../actions';
import {SelectedComponentState} from 'src/app/state/lib/selected-component.state';
import {SelectedConfigurationsState} from 'src/app/state/lib/selected-configuration.state';
import {SelectedRequirementState} from 'src/app/state/lib/selected-requirement.state';
import {
  AnalyticsEventPayload,
  SubmitAnalyticsEventAction,
} from '@ansys-internal/awf-angular/analytics';
import {AnalyticEventEnum} from '../../../shared/enums';
import {SelectComponent} from '@ansys-internal/awc-angular/forms';
import {SharedModule} from '../../../shared/shared.module';
import {CommonModule, TitleCasePipe} from '@angular/common';
import {ButtonComponent} from '@ansys-internal/awc-angular/buttons';
import {DialogRef} from '@angular/cdk/dialog';
import {ConceptUnitService} from '../../../shared/services/unit.service';

@Component({
    selector: 'app-units',
    templateUrl: './units.component.html',
    styleUrls: ['./units.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [UnitNameTransformPipe],
    imports: [
        SelectComponent,
        SharedModule,
        TitleCasePipe,
        ButtonComponent,
        CommonModule,
    ]
})
export class UnitsComponent implements OnInit {
  protected unitChoiceLabels: string[] = [];
  protected unitChoiceMap: Map<string, AWCListContent[]> = new Map();
  protected spinner: Icons = Icons.SPINNER;
  protected loading: boolean = true;
  protected layout: FlexLayout = FlexLayout.COLUMN;
  protected choices: Record<string, UnitChoice> = {};
  protected selectedSection: ConceptSections = ConceptSections.CONFIGURATIONS;
  private selectedStateFromType: Map<ConceptSections, Type<StateNode<string[]>>> = new Map([
    [ConceptSections.CONFIGURATIONS, SelectedConfigurationsState],
    [ConceptSections.COMPONENT, SelectedComponentState],
    [ConceptSections.REQUIREMENT, SelectedRequirementState],
  ]);
  private analyticData: { [key: string]: any } = [];

  constructor(
    private unitService: UnitChoicesService,
    private conceptService: ConceptService,
    private conceptUnitService: ConceptUnitService,
    private snackbar: SnackBarService,
    private state: State,
    @Inject(DIALOG_REF) private _dialogRef: DialogRef,
    private actions: ActionContributionsService,
    private _cdr: ChangeDetectorRef
  ) {}

  async ngOnInit(): Promise<void> {
    this.loading = true;
    const unitChoices = await lastValueFrom(this.unitService.readUnitChoicesGet());

    this.choices = unitChoices.unit_type_to_unit_map as Record<string, UnitChoice>;
    this.unitChoiceLabels = Object.keys(this.conceptUnitService.unitInfo);
    this.unitChoiceMap = new Map(
      Object.entries(this.conceptUnitService.unitInfo).map(([type, options]) => [
        type,
        Object.keys(options).map((opt) => ({
          id: opt === '' ? '' : opt,
          text: opt === '' ? 'No Unit' : opt,
        })),
      ])
    );

    this.loading = false;
    this._cdr.detectChanges();
  }

  protected selectionChanged($event: AWCListItem[], unit: string): void {
    this.analyticData[unit] = $event;
    this.choices[unit] = $event[0].id as UnitChoice;
  }

  protected onSubmit(): void {
    this.snackbar.info('Updating Unit Choices!');
    this.loading = true;
    this.unitService
      .updateUnitChoicesPut({ unit_type_to_unit_map: this.choices })
      .subscribe(() => {
        this.conceptService.clearConceptsAndFetch().then(async () => {
          this.loading = false;
          this._dialogRef.close();
          const tab = this.state.value(ActiveTabState);

          // Fire analytics event
          const eventData: AnalyticsEventPayload = {
            type: AnalyticEventEnum.UNITS_UPDATED,
            data: this.analyticData,
          };
          this.actions.execute(SubmitAnalyticsEventAction, eventData);

          this.snackbar.success('Unit Choices Updated!');
          const state = this.selectedStateFromType.get(this.selectedSection);
          if (state) {
            const selection = this.state.value(state, tab?.id);
            this.state.set(state, [], tab?.id);
            this.state.set(state, selection, tab?.id);
          }
          this.actions.execute(UpdateInputs);
        });
      });
  }
}
